import { defineStore } from 'pinia';
import { streamTrademarksOfUser, updateTrademark } from "@/services/trademarks.js";
import { streamDocketingOfUser, getDocketTasksByTrademarkSerialNumberByDeadline, updateDocketTask } from "@/services/trademark_docketing.js";
import { getTrademarkLogsBySerialNumber, updateTrademarkLog } from "@/services/logging.js";
import { db } from "@/services/db.js";
import { ref } from "vue";

export const useTrademarkStore = defineStore('trademark', {
  state: () => ({
    firmId: null,
    userId: null,
    the_trademarks: [],
    currentTrademarkId: null,
    the_dockets: [],
    tm_loaded: false,
    docket_loaded: false,
    unsubscribeTm: null,
    unsubscribeDocketing: null,
    trademark_columns:[
      {
        name: "Serial No.",
        sortStatus: "a",
        checked: true,
        fieldName: "serialNumber",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Registration No.",
        sortStatus: "a",
        checked: true,
        fieldName: "registrationNumber",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Mark",
        sortStatus: "a",
        checked: true,
        fieldName: "wordMark",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "IC",
        sortStatus: "a",
        checked: true,
        fieldName: "internationalClass",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Applicant",
        sortStatus: "a",
        checked: true,
        fieldName: "applicantName",
        filterStr: "",
        filterFlag: false,
      },
      {
        // display-only: alive is boolean, which the string-based sort/filter
        // helpers can't handle
        name: "Status",
        sortStatus: "",
        checked: true,
        fieldName: "alive",
        filterStr: "",
        filterFlag: false,
        nosort: true,
        noFilter: true,
      },
      {
        // display-only: the doc stores the client id; the row resolves the
        // name via a lookup (no denormalized clientName like patent-views)
        name: "Client",
        sortStatus: "",
        checked: true,
        fieldName: "client",
        filterStr: "",
        filterFlag: false,
        nosort: true,
        noFilter: true,
      },
      {
        // display-only: uid list resolved to names by the row
        name: "Assigned",
        sortStatus: "",
        checked: true,
        fieldName: "assigned",
        filterStr: "",
        filterFlag: false,
        nosort: true,
        noFilter: true,
      },
    ],
    the_docket_columns: [
      {
        name: "Serial No.",
        sortStatus: "a",
        checked: true,
        fieldName: "serialNumber",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Registration No.",
        sortStatus: "a",
        checked: true,
        fieldName: "registrationNumber",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Mark",
        sortStatus: "a",
        checked: true,
        fieldName: "wordMark",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Task",
        sortStatus: "a",
        checked: true,
        fieldName: "title",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Deadline",
        sortStatus: "a",
        checked: true,
        fieldName: "deadline",
        filterStr: "",
        filterFlag: false,
        // date-range filter (start/end pickers) instead of the free-text box,
        // same design as patent docketing. Defaults span everything so
        // enabling the filter alone never hides a deadline
        dateRange: true,
        filterStart: "1970-01-01",
        filterEnd: "9999-12-31",
      },
      {
        name: "Event Type",
        sortStatus: "a",
        checked: true,
        fieldName: "eventType",
        filterStr: "",
        filterFlag: false,
        filterOptions: [],
      },
      {
        name: "Status",
        sortStatus: "a",
        checked: true,
        fieldName: "status",
        filterStr: "",
        filterFlag: false,
        filterOptions: [],
      },
      {
        name: "Action",
        sortStatus: "",
        checked: true,
        fieldName: "action",
        filterStr: "",
        filterFlag: false,
        nosort: true,
      },
    ],
    showCompleted: false,
    showDead: false,
    showArchived: false,
    trademark_sort: null,
    docket_sort: null,
  }),
  getters: {
    filteredSortedTrademarks: (state) => {
      let filterColumns = state.trademark_columns.filter((c) => { return c.filterFlag == true })
        .map((c) => {
          if (c.filterOptions) {
            c.filterArray = c.filterOptions.reduce((optArray, opt) => {
              if (opt.value) {
                optArray.push(opt.label);
              }
              return optArray;
            }, []);
          }
          return c;
        });

      let filteredTms = []

      if (filterColumns.length == 0) {
        filteredTms = state.the_trademarks.filter((x) => {
          if (state.showArchived) {
            return !!x.archived;
          } else if (state.showDead) {
            // "also show dead": alive and dead together, still hiding archived
            return !x.archived;
          } else {
            return x.alive && !x.archived;
          }
        });
      } else {
        filteredTms = state.the_trademarks.filter((x) => {
          if (state.showArchived) {
            if (!x.archived) return false;
          } else if (state.showDead) {
            if (x.archived) return false;
          } else {
            if (!x.alive || x.archived) return false;
          }
          for (const filterColumn of filterColumns) {
            var field_name = filterColumn.fieldName;

            let value = "";
            if (Array.isArray(x[field_name])) {
              value = x[field_name].join(" ");
            }
            else {
              value = x[field_name];
            }
            if (filterColumn.filterArray && !filterColumn.filterArray.includes(value)) {
              return false;
            } else {
              if (!value || !value.toUpperCase().includes(filterColumn.filterStr.toUpperCase())) {
                return false;
              }
            }
          }
          return true;
        });
      }
      let localSortColumn = state.trademark_sort;
      if (localSortColumn) {
        return filteredTms.toSorted(function (a, b) {
          var keyA = "";
          var keyB = "";
          if (Array.isArray(a[localSortColumn.fieldName])) {
            keyA = a[localSortColumn.fieldName][0] ? a[localSortColumn.fieldName][0] : "";
            keyB = b[localSortColumn.fieldName][0] ? b[localSortColumn.fieldName][0] : "";
          }
          else {
            keyA = a[localSortColumn.fieldName] ? a[localSortColumn.fieldName] : "";
            keyB = b[localSortColumn.fieldName] ? b[localSortColumn.fieldName] : "";
          }
          if (localSortColumn.sortStatus == "a") {
            return keyA.localeCompare(keyB);
          } else {
            return keyB.localeCompare(keyA);
          }
        });
      } else {
        return filteredTms;
      }
    },
    filteredSortedDockets: (state) => {
      let filterColumns = state.the_docket_columns.filter((c) => { return c.filterFlag == true })
        .map((c) => {
          if (c.filterOptions) {
            c.filterArray = c.filterOptions.reduce((optArray, opt) => {
              if (opt.value) {
                optArray.push(opt.label);
              }
              return optArray;
            }, []);
          }
          return c;
        });

      let filteredDockets = []

      if (filterColumns.length == 0) {
        filteredDockets = state.the_dockets.filter((x) => {
          if (x.archived) return false;
          if (state.showCompleted) {
            return x.status == "Completed";
          } else {
            return x.status != "Completed";
          }
        });
      } else {
        filteredDockets = state.the_dockets.filter((x) => {
          if (x.archived) return false;

          if (state.showCompleted != (x.status == "Completed")) {
            return false;
          }
          for (const filterColumn of filterColumns) {
            var field_name = filterColumn.fieldName;

            // date-range columns (deadline): inclusive [start, end], either
            // side optional; tasks with no deadline are excluded while a
            // range filter is active
            if (filterColumn.dateRange) {
              const v = x[field_name];
              if (!v) return false;
              if (filterColumn.filterStart && v < filterColumn.filterStart) return false;
              if (filterColumn.filterEnd && v > filterColumn.filterEnd) return false;
              continue;
            }

            let value = "";
            if (Array.isArray(x[field_name])) {
              value = x[field_name].join(" ");
            }
            else {
              value = x[field_name];
            }

            if (filterColumn.filterArray && !filterColumn.filterArray.includes(value)) {
              return false;
            } else {
              if (!value || !value.toUpperCase().includes(filterColumn.filterStr.toUpperCase())) {
                return false;
              }
            }
          }
          return true;
        });
      }
      let localSortColumn = state.docket_sort;
      if (localSortColumn) {
        return filteredDockets.toSorted(function (a, b) {
          var keyA = "";
          var keyB = "";
          if (Array.isArray(a[localSortColumn.fieldName])) {
            keyA = a[localSortColumn.fieldName][0] ? a[localSortColumn.fieldName][0] : "";
            keyB = b[localSortColumn.fieldName][0] ? b[localSortColumn.fieldName][0] : "";
          }
          else {
            keyA = a[localSortColumn.fieldName] ? a[localSortColumn.fieldName] : "";
            keyB = b[localSortColumn.fieldName] ? b[localSortColumn.fieldName] : "";
          }
          if (localSortColumn.sortStatus == "a") {
            return keyA.localeCompare(keyB);
          } else {
            return keyB.localeCompare(keyA);
          }
        });
      } else {
        return filteredDockets;
      }
    },
    currentTrademark: (state) => {
      if (!state.currentTrademarkId) {
        return null;
      }
      return state.the_trademarks.find((tm) => tm.serialNumber === state.currentTrademarkId) || null;
    },
    tm_columns: (state) => state.trademark_columns,
    tm_sort_column: (state) => state.trademark_sort,
    docket_columns: (state) => state.the_docket_columns,
    docket_sort_column: (state) => state.docket_sort,
  },
  actions: {


    loadData(firmId, userId) {
      if (!firmId || !userId) {
        throw new Error('firm and user required');
      }
      const columnChecks = JSON.parse(localStorage.getItem("trademark_docketing_columns_checked"));
      if (columnChecks && Array.isArray(columnChecks)) {
        this.the_docket_columns.forEach((c, index) => { c.checked = columnChecks[index]; });
      }
      const tmcolumnChecks = JSON.parse(localStorage.getItem("trademark_columns_checked"));
      if (tmcolumnChecks && Array.isArray(tmcolumnChecks)) {
        // guard against stale saved arrays when columns are added/reordered —
        // an out-of-range entry would otherwise silently uncheck the column
        this.trademark_columns.forEach((c, index) => {
          if (typeof tmcolumnChecks[index] === 'boolean') c.checked = tmcolumnChecks[index];
        });
      }
      if (firmId != this.firmId || userId != this.userId) {
        //get rid of loaded at code. maybe need error handling to reset when the listener fails.
        this.tm_loaded = false;
        this.docket_loaded = false;
        this.firmId = firmId;
        this.userId = userId;
        if (this.unsubscribeTm && typeof this.unsubscribeTm === 'function') {
          this.unsubscribeTm();
          this.unsubscribeTm = null;
        }
        if (this.unsubscribeDocketing && typeof this.unsubscribeDocketing === 'function') {
          this.unsubscribeDocketing();
        }
        // streamTrademarksOfUser is async (role lookup first); guard against a
        // firm/user switch happening before the subscription resolves
        streamTrademarksOfUser(firmId, userId, this.tradeMarkSnapper).then((unsub) => {
          if (this.firmId !== firmId || this.userId !== userId) {
            unsub();
          } else {
            this.unsubscribeTm = unsub;
          }
        });
        // docketing tasks are readable by assigned clients too (same rules model
        // as patent docketing) — stream for EVERY role; streamDocketingOfUser
        // scopes non-manager/admin roles (member and client alike) by assigned[]
        streamDocketingOfUser(firmId, userId, this.docketSnapper).then((unsub) => {
          if (this.firmId !== firmId || this.userId !== userId) {
            unsub();
          } else {
            this.unsubscribeDocketing = unsub;
          }
        });
        //TODO do unsubscribe/resubscribe on errors. maybe on leaving the page and resubscribe on load?
        
      }
    },
    docketSnapper(snapshot) {
      const all_dockets = ref([]);
      all_dockets.value = snapshot.docs.map((doc) => {
        const data = doc.data();
        if (data.status === "Overdue") {
          data.status = "To Do";
        }
        return data;
      });
      this.the_dockets = all_dockets;
      this.docket_loaded = true;
      this.updateFilterOptions();
    },
    tradeMarkSnapper(snapshot) {
      const tms = ref([]);
      tms.value = snapshot.docs.map((doc) => {
        return doc.data();
      });
      this.tm_loaded = true;
      this.the_trademarks = tms;
    },
    updateCurrentTrademark(serialNumber) {
      this.currentTrademarkId = serialNumber;
    },
    async updateAssigned(serialNumber, assigned) {
      // assignment is denormalized onto the mark's docketing tasks and logs
      // (same pattern as patents) so firestore.rules can scope member reads
      const firmId = this.firmId;
      try {
        await db.runTransaction(async (transaction) => {
          await updateTrademark(firmId, serialNumber, { "assigned": assigned }, transaction);

          const docketTasks = await getDocketTasksByTrademarkSerialNumberByDeadline(firmId, serialNumber);
          for (const docketTask of docketTasks.value) {
            await updateDocketTask(firmId, docketTask.id, { "assigned": assigned }, transaction);
          }

          const logs = await getTrademarkLogsBySerialNumber(firmId, serialNumber);
          for (const log of logs.value) {
            await updateTrademarkLog(firmId, log.id, { "assigned": assigned }, transaction);
          }
        });
      } catch (error) {
        console.error("Transaction failed: ", error);
        throw error;
      }
    },
    updateClientOfTrademark(serialNumber, clientId) {
      return updateTrademark(this.firmId, serialNumber, { "client": clientId });
    },
    updateTmColumns(newColumns) {
      this.trademark_columns = newColumns;
    },
    updateDocketColumns(newColumns) {
      this.the_docket_columns = newColumns;
    },
    setShowDead(newVal) {
      this.showDead = !!newVal;
      if (this.showDead) {
        this.showArchived = false;
      }
    },
    setShowArchived(newVal) {
      this.showArchived = !!newVal;
      if (this.showArchived) {
        this.showDead = false;
      }
    },
    async updateArchived(serialNumber, archived) {
      const firmId = this.firmId;
      try {
        await db.runTransaction(async (transaction) => {
          await updateTrademark(firmId, serialNumber, { "archived": archived }, transaction);

          const docketTasks = await getDocketTasksByTrademarkSerialNumberByDeadline(firmId, serialNumber);
          for (const docketTask of docketTasks.value) {
            await updateDocketTask(firmId, docketTask.id, { "archived": archived, "status": "To Do" }, transaction);
          }

          const logs = await getTrademarkLogsBySerialNumber(firmId, serialNumber);
          for (const log of logs.value) {
            await updateTrademarkLog(firmId, log.id, { "archived": archived }, transaction);
          }
        });
      } catch (error) {
        console.error("Transaction failed: ", error);
        throw error;
      }
    },
    setShowCompleted(newVal) {
      this.showCompleted = !!newVal;
      this.updateFilterOptions();
    },
    updateTmSortColumn(newVal) {
      this.trademark_sort = newVal;
    },
    updateDocketSort(newVal) {
      this.docket_sort = newVal;
    },
    updateFilterOptions() {
      console.log("Updating filter options...");
      this.updateEventTypeFilterOptions();
      this.updateStatusFilterOptions();
    },

    updateEventTypeFilterOptions() {
      // Get unique eventTypes from docketingItems
      const eventTypeColumn = this.the_docket_columns.find(col => col.fieldName === 'eventType');
      if (!eventTypeColumn) return;

      // Preserve existing filter selections
      const existingSelections = {};
      if (eventTypeColumn.filterOptions) {
        eventTypeColumn.filterOptions.forEach(opt => {
          existingSelections[opt.label] = opt.value;
        });
      }

      if (!this.showCompleted) {
        const pendingItems = this.the_dockets.filter(item => item.status != "Completed");
        const uniqueEventTypes = [...new Set(
          pendingItems
            .map(item => item.eventType)
            .filter(eventType => eventType) // filter out null/undefined
        )].sort();

        eventTypeColumn.filterOptions = uniqueEventTypes.map(eventType => ({
          label: eventType,
          // Preserve existing selection if it exists, otherwise default to true
          value: Object.hasOwn(existingSelections, eventType) ? existingSelections[eventType] : true,
          count: pendingItems.filter(item => item.eventType === eventType).length
        }));
      } else {
        const completedItems = this.the_dockets.filter(item => item.status == "Completed");
        const uniqueEventTypes = [...new Set(
          completedItems
            .map(item => item.eventType)
            .filter(eventType => eventType) // filter out null/undefined
        )].sort();

        eventTypeColumn.filterOptions = uniqueEventTypes.map(eventType => ({
          label: eventType,
          // Preserve existing selection if it exists, otherwise default to true
          value: Object.hasOwn(existingSelections, eventType) ? existingSelections[eventType] : true,
          count: completedItems.filter(item => item.eventType === eventType).length
        }));
      }
    },

    updateStatusFilterOptions() {
      const statusColumn = this.the_docket_columns.find(col => col.fieldName === 'status');
      if (!statusColumn) return;

      // Preserve existing filter selections
      const existingSelections = {};
      if (statusColumn.filterOptions) {
        statusColumn.filterOptions.forEach(opt => {
          existingSelections[opt.label] = opt.value;
        });
      }

      if (!this.showCompleted) {
        const pendingItems = this.the_dockets.filter(item => item.status != "Completed");
        const uniqueStatusTypes = [...new Set(
          pendingItems
            .map(item => item.status)
            .filter(status => status && status !== "Overdue") // filter out null/undefined and "Overdue"
        )].sort();

        statusColumn.filterOptions = uniqueStatusTypes.map(status => ({
          label: status,
          // Preserve existing selection if it exists, otherwise default to true
          value: Object.hasOwn(existingSelections, status) ? existingSelections[status] : true,
          count: pendingItems.filter(item => { 
            if (status === "To Do" && (item.status === "Overdue" || item.status === "To Do")) {
              return true;
            }
            if (status === "Overdue") {
              return false;
            }
            return item.status === status;
          }).length
        }));
      } else {
        statusColumn.filterOptions = [
          { 
            label: "Completed", 
            value: Object.hasOwn(existingSelections, "Completed") ? existingSelections["Completed"] : true, 
            count: this.the_dockets.filter(item => item.status == "Completed").length 
          }
        ];
      }
    }
  },
});


